import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import * as crawlerApi from 'api/crawlerApi';
import * as menuApi from 'api/menuApi';
import { useAppDispatch, useAppSelector } from 'store/hooks/useStore';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import {
  menuRegistrationActions,
  menuRegistrationSelectors,
} from 'store/reducers/menuCategoryReducer/menuRegistrationSlice';
import { Crawler } from 'types/crawler';
import { REGISTRATION_STEP } from 'constants/registrationStep';
import { useToast } from 'hooks/useToast';
import {
  createMenuRegistrationFormFields,
  mapFormFieldsToMenuRegistrationPayload,
  MenuRegistrationFormFields,
} from '../types/menuRegistrationFormFields';
import { useListStore } from './useListStore';
import { useMenuCrawlers } from './useMenuCrawlers';

/**
 * メニュー登録 store の実装をコンポーネントから隠すための中間層
 */
export const useMenuRegistrationStore = () => {
  const dispatch = useAppDispatch();
  const { fetchMenuCategories } = useListStore();
  const toast = useToast();
  const [crawlers, setCralwers] = useState<Crawler[]>([]);
  const form = useForm<MenuRegistrationFormFields>({
    defaultValues: createMenuRegistrationFormFields({}),
    mode: 'all',
  });
  const { getValues, reset, handleSubmit } = form;

  const isOpenModal = useAppSelector(menuRegistrationSelectors.isOpenModal);
  const categoryId = useAppSelector(menuRegistrationSelectors.categoryId);
  const currentModalStep = useAppSelector(
    menuRegistrationSelectors.currentModalStep,
  );

  const {
    menuCrawlers,
    setMenuCrawlers,
    onAddCrawler,
    onDeleteCrawler,
    onSortCrawler,
  } = useMenuCrawlers();

  const onCloseModal = useCallback(() => {
    dispatch(menuRegistrationActions.initialize());
    reset();
    setCralwers([]);
    setMenuCrawlers([]);
  }, [dispatch, reset, setMenuCrawlers]);

  const onCloseModalWhenCompleted = () => {
    onCloseModal();
    void fetchMenuCategories();
  };

  const onConfirm = () => {
    dispatch(
      menuRegistrationActions.updateStep(REGISTRATION_STEP.CONFIRMATION),
    );
  };

  const onGoBack = () => {
    dispatch(menuRegistrationActions.updateStep(REGISTRATION_STEP.INPUT));
  };

  const onSubmit = handleSubmit(
    async (values: MenuRegistrationFormFields) => {
      dispatch(loaderActions.setLoading(true));
      const payload = {
        ...mapFormFieldsToMenuRegistrationPayload(values),
        categoryId,
      };

      const result = await menuApi.addMenu(payload);
      if (result.ok) {
        const menuId = result.value.id;
        const payloadMenu = menuCrawlers.map((crawler) => ({
          ...crawler,
          menuId,
        }));

        const { ok } = await menuApi.editMenuCrawlers(menuId, payloadMenu);
        if (ok) {
          dispatch(
            menuRegistrationActions.updateStep(REGISTRATION_STEP.RESULT),
          );
        } else {
          toast({
            status: 'error',
            description: 'メニューに紐づくクローラーの登録に失敗しました。',
          });
          onCloseModalWhenCompleted();
        }
      } else {
        toast({
          status: 'error',
          description: 'メニューの登録に失敗しました。',
        });
      }
      dispatch(loaderActions.setLoading(false));
    },
    () => {
      toast({
        status: 'error',
        description: 'メニューの登録に失敗しました。',
      });
    },
  );

  useEffect(() => {
    void (async () => {
      if (isOpenModal) {
        const result = await crawlerApi.fetchCrawlers();
        if (result.ok) {
          setCralwers(result.value);
        } else {
          toast({
            status: 'error',
            description: `クローラーの取得に失敗しました: ${result.err.message}`,
          });
          onCloseModal();
        }
      }
    })();
  }, [isOpenModal, toast, onCloseModal]);

  return {
    /** メニュー登録モーダル表示状態 */
    isOpenModal,
    /** メニュー登録モーダルの現ステップ */
    currentModalStep,
    /** モーダル閉じる */
    onCloseModal,
    /** 登録完了後モーダル閉じる */
    onCloseModalWhenCompleted,
    /** 確認画面へ */
    onConfirm,
    /** 入力画面へ戻る */
    onGoBack,
    /** 入力を送信 */
    onSubmit,
    /** フォーム */
    form,
    /** フォーム values */
    values: getValues(),
    /** クローラー一覧 */
    crawlers,
    /** メニューに紐づくクローラー */
    menuCrawlers,
    /** クローラー追加 */
    onAddCrawler,
    /** クローラー削除 */
    onDeleteCrawler,
    /** クローラー並び替え */
    onSortCrawler,
  };
};
